import type { ComponentProps, ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@shared/lib';

/**
 * Field shell — token-styled, matches the Figma Input set (48px / rounded-lg / 1px border).
 * Figma field text is Figtree (`font-display`) at 16px with -0.16px tracking; 20px inset.
 */
export const inputVariants = cva(
  'flex h-12 w-full rounded-lg border bg-surface-raised px-5 font-display text-base tracking-[-0.16px] text-fg transition-colors placeholder:text-fg/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      tone: {
        default: 'border-border-soft aria-invalid:border-danger',
        /** Filled / success ("View breakdown" / "View Attachment") — Figma uses 8px radius. */
        success: 'rounded-md border-success-border bg-success-soft',
        /** Empty action ("Click to add breakdown") — Figma uses 8px radius. */
        action: 'rounded-md border-border-strong text-info placeholder:text-info',
      },
    },
    defaultVariants: { tone: 'default' },
  },
);

export interface InputProps
  extends Omit<ComponentProps<'input'>, 'size'>,
    VariantProps<typeof inputVariants> {
  /** Decorative element inside the field, before the text (e.g. a currency icon). */
  startIcon?: ReactNode;
  /** Decorative/interactive element inside the field, after the text (e.g. eye / clear). */
  endIcon?: ReactNode;
}

/**
 * Text input. Renders a bare `<input>` unless `startIcon`/`endIcon` are provided, in which case
 * it wraps in a relative container and pads the field. Colours come from tokens only.
 */
export function Input({ className, tone, startIcon, endIcon, ...props }: InputProps) {
  if (!startIcon && !endIcon) {
    return (
      <input data-slot="input" className={cn(inputVariants({ tone }), className)} {...props} />
    );
  }
  return (
    <div data-slot="input-wrapper" className="relative w-full">
      {startIcon ? (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute left-5 top-1/2 flex -translate-y-1/2 items-center text-muted-soft"
        >
          {startIcon}
        </span>
      ) : null}
      <input
        data-slot="input"
        className={cn(inputVariants({ tone }), startIcon && 'pl-11', endIcon && 'pr-11', className)}
        {...props}
      />
      {endIcon ? (
        <span className="absolute right-5 top-1/2 flex -translate-y-1/2 items-center text-muted-soft">
          {endIcon}
        </span>
      ) : null}
    </div>
  );
}
